import React, { useState, useEffect } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Check, Messages, Star } from '@saleshandy/icons';
import { IProps } from './types';
import Button from '../../../../../../../../shared/design-system/components/atoms/button';
import {
  getButtonLabel,
  getDialerPlanMonthlyPrice,
  getDialerPlanQuarterlyPrice,
  getDialerPlanYearlyPrice,
  getPlanDetailsObj,
  getPlanPrice,
  getIsUserInPaidDialerPlan,
} from './utils/helper';
import { SelectPlanCTAText } from '../../../../../../../../shared/design-system/components/molecules/pricing-card/types';
import { OverlayTooltip } from '../../../../../../../../shared/design-system/components/overlay';
import { isAnnuallyPlan, isQuarterlyPlan } from '../../utils/helper';

const DialerPlanCard: React.FC<IProps> = ({
  plan,
  isPurchaseInProgress,
  handlePlanSelect,
  sendGetDialerSubscriptionResponse,
  sendGetDialerPlansResponse,
  planCycle,
}) => {
  const { t } = useTranslation();

  const isUserSubscribed = getIsUserInPaidDialerPlan(
    sendGetDialerSubscriptionResponse?.planCode,
  );

  const [planDetails, setPlanDetails] = useState(
    getPlanDetailsObj(plan, isUserSubscribed),
  );

  const btnLabel = getButtonLabel({
    plan: planDetails,
    sendGetDialerSubscriptionResponse,
    planCycle,
  });

  const getButtonLabelTxt = () => btnLabel;

  const getButtonTheme = () => {
    if (
      planDetails?.isBestValuePlan &&
      btnLabel !== SelectPlanCTAText.CurrentPlan &&
      btnLabel !== SelectPlanCTAText.DowngradePlan
    ) {
      return Button.Theme.Solid;
    }

    return Button.Theme.Ghost;
  };

  const getPlanDiscountText = () => {
    if (isAnnuallyPlan(planCycle)) {
      return `Save $${
        (getDialerPlanMonthlyPrice(
          planDetails.planCode,
          sendGetDialerPlansResponse,
        ) -
          planDetails.price) *
        planDetails?.planMode
      } annually`;
    }
    if (isQuarterlyPlan(planCycle)) {
      return `Save $${
        (getDialerPlanQuarterlyPrice(
          planDetails.planCode,
          sendGetDialerPlansResponse,
        ) -
          planDetails.price) *
        planDetails?.planMode
      } annually`;
    }
    return `Save $${
      (planDetails.price -
        getPlanPrice(
          getDialerPlanYearlyPrice(
            planDetails.planCode,
            sendGetDialerPlansResponse,
          ),
          12,
        )) *
      12
    } on annual plan`;
  };

  const cardClassName = classnames([
    'dialer-plan-card',
    {
      'dialer-plan-best-value': planDetails?.isBestValuePlan,
    },
  ]);

  const btnClassName = classnames([
    {
      'dialer-ghost-primary': !planDetails?.isBestValuePlan,
    },
    {
      'dialer-ghost-primary-disable':
        btnLabel === SelectPlanCTAText.DowngradePlan,
    },
    {
      'dialer-solid-primary':
        btnLabel !== SelectPlanCTAText.CurrentPlan &&
        btnLabel !== SelectPlanCTAText.DowngradePlan &&
        planDetails?.isBestValuePlan,
    },
    {
      'dialer-ghost-secondary-disable':
        btnLabel === SelectPlanCTAText.CurrentPlan,
    },
  ]);

  useEffect(() => {
    if (plan) {
      setPlanDetails(getPlanDetailsObj(plan, isUserSubscribed));
    }
  }, [plan, isUserSubscribed]);

  return (
    <div className={cardClassName}>
      <div className="dialer-plan-best-value__labels-wrappers">
        {planDetails?.isBestValuePlan && (
          <div className="dialer-plan-best-value__labels">
            <Star color="#FFFFFF" />
            <span className="dialer-plan-best-value__labels-text">
              {t('labels.most_popular')}
            </span>
          </div>
        )}
      </div>
      <div className="dialer-plan-card__body">
        <div className="card-body">
          <div className="dialer-plan-card__details mb-2">
            <div className="dialer-plan-card__details-header">
              <span className="dialer-plan-card__details-title">
                {planDetails?.displayName}
              </span>
            </div>
            <div className="dialer-plan-card__price-wrapper">
              {planDetails?.price ? (
                <>
                  <div className="dialer-plan-card__price-currency">
                    <span className="dialer-plan-card__price-amount">
                      ${Math.round(planDetails?.price)}
                    </span>
                    {(isAnnuallyPlan(planCycle) ||
                      isQuarterlyPlan(planCycle)) && (
                      <span className="dialer-plan-card__price-strikethrough">
                        $
                        {getDialerPlanMonthlyPrice(
                          planDetails?.planCode,
                          sendGetDialerPlansResponse,
                        )}
                      </span>
                    )}
                    <span className="dialer-plan-card__price-suffix">
                      /Month
                    </span>
                  </div>
                  <div className="dialer-plan-card__price-discount">
                    <span className="dialer-plan-card__price-discount-text">
                      {getPlanDiscountText()}
                    </span>
                  </div>
                </>
              ) : (
                <div className="mt-1">
                  <Messages
                    width={28}
                    height={28}
                    className="popover-arrow-color-txt"
                  />
                </div>
              )}
            </div>

            <Button
              variant={Button.Variant.Primary}
              theme={getButtonTheme()}
              onClick={() => {
                handlePlanSelect(planDetails?.id, planDetails?.planName);
              }}
              className={`w-100 ${btnClassName}`}
              isLoading={isPurchaseInProgress}
              disabled={
                btnLabel === SelectPlanCTAText.CurrentPlan ||
                btnLabel === SelectPlanCTAText.DowngradePlan ||
                isPurchaseInProgress
              }
            >
              {getButtonLabelTxt()}
            </Button>
          </div>

          <div className="dialer-plan-card__features">
            <ul className="dialer-plan-card__feature-list">
              {planDetails?.features?.map(
                ({
                  label,
                  labelClass,
                  tooltipText,
                  strikeText,
                  highlight,
                  tooltipClassName,
                }) => (
                  <li
                    key={label}
                    className={classnames([
                      'dialer-plan-card__feature-item',
                      {
                        'dialer-plan-card__feature-item--highlight': highlight,
                      },
                    ])}
                  >
                    <span className="dialer-plan-card__feature-check">
                      <Check className="dialer-plan-card__check-icon" />
                    </span>
                    <>
                      {tooltipText ? (
                        <OverlayTooltip
                          text={tooltipText}
                          className={tooltipClassName}
                        >
                          <span
                            className={classnames([
                              'dialer-plan-card__feature-label',
                              'underline-dashed',
                              labelClass,
                            ])}
                          >
                            {strikeText && (
                              <span className="regular-1 gray-txt-15 mr-1 text-decoration-line-through">
                                {strikeText}
                              </span>
                            )}
                            {label}
                          </span>
                        </OverlayTooltip>
                      ) : (
                        <span
                          className={classnames([
                            'dialer-plan-card__feature-label',
                            labelClass,
                          ])}
                        >
                          {strikeText && (
                            <span className="regular-1 gray-txt-15 mr-1 text-decoration-line-through">
                              {strikeText}
                            </span>
                          )}
                          {label}
                        </span>
                      )}
                    </>
                  </li>
                ),
              )}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DialerPlanCard;
